/**
 * Adapter HTTP /api/v1/provider-keys (Sprint 9).
 * Pattern minimal : fetch + erreur typée. Cf. routes-api.http.ts pour
 * la version full avec parsing détaillé.
 */
import {
    ProviderKeyError,
    type CreateProviderKeyInput,
    type ModelInfo,
    type ProviderKey,
    type ProviderKeyErrorCode,
    type ProviderKeyModels,
    type UpdateProviderKeyInput,
} from '@/domain/provider-keys'

import { appendActiveTenant, applyActiveTenantToBody, readActiveTenantId } from './_active-tenant'

interface ProviderKeyDTO {
    id: string
    tenant_id: string
    name: string
    provider_type: string
    base_url?: string
    electricity_zone?: string
    disabled: boolean
    created_at: string
    updated_at: string
}

function parseProviderKey(raw: unknown): ProviderKey {
    const dto = raw as ProviderKeyDTO
    return {
        id: dto.id,
        tenantId: dto.tenant_id,
        name: dto.name,
        providerType: dto.provider_type as ProviderKey['providerType'],
        baseUrl: dto.base_url ?? '',
        electricityZone: dto.electricity_zone ?? '',
        disabled: dto.disabled,
        createdAt: dto.created_at,
        updatedAt: dto.updated_at,
    }
}

async function handleError(resp: Response): Promise<never> {
    let code: ProviderKeyErrorCode = 'unknown'
    let message = `HTTP ${resp.status}`
    try {
        const body = await resp.json()
        if (body?.error?.code) code = body.error.code as ProviderKeyErrorCode
        if (body?.error?.message) message = body.error.message
    } catch {
        // Pas de JSON, garde le message HTTP par défaut.
    }
    throw new ProviderKeyError(code, message, resp.status)
}

export class HttpProviderKeysApi {
    private readonly baseURL: string

    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async list(tenantId?: string): Promise<ProviderKey[]> {
        // Priorité au tenantId explicite (rare) ; sinon, override actif du
        // super_admin via appendActiveTenant.
        const params = new URLSearchParams()
        if (tenantId) {
            params.set('tenant_id', tenantId)
        } else {
            appendActiveTenant(params)
        }
        const query = params.toString()
        const url = query
            ? `${this.baseURL}/api/v1/provider-keys?${query}`
            : `${this.baseURL}/api/v1/provider-keys`
        const resp = await fetch(url, { credentials: 'include' })
        if (!resp.ok) await handleError(resp)
        const body = (await resp.json()) as { items: unknown[] }
        return body.items.map(parseProviderKey)
    }

    async create(input: CreateProviderKeyInput, csrfToken: string): Promise<ProviderKey> {
        // Si super_admin a un tenant override actif et l'input ne contient
        // pas explicitement de tenantId, on force le tenant override.
        const effectiveTenantId = input.tenantId || readActiveTenantId() || input.tenantId
        const payload = applyActiveTenantToBody({
            tenant_id: effectiveTenantId,
            name: input.name,
            provider_type: input.providerType,
            base_url: input.baseUrl ?? '',
            electricity_zone: input.electricityZone ?? '',
            credential_secret_base64: input.credentialSecretBase64,
        })
        const resp = await fetch(`${this.baseURL}/api/v1/provider-keys`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify(payload),
        })
        if (!resp.ok) await handleError(resp)
        const body = (await resp.json()) as { provider_key: unknown }
        return parseProviderKey(body.provider_key)
    }

    async update(id: string, input: UpdateProviderKeyInput, csrfToken: string): Promise<ProviderKey> {
        const payload: Record<string, unknown> = {}
        if (input.name !== undefined) payload.name = input.name
        if (input.baseUrl !== undefined) payload.base_url = input.baseUrl
        if (input.electricityZone !== undefined) payload.electricity_zone = input.electricityZone
        if (input.disabled !== undefined) payload.disabled = input.disabled
        const resp = await fetch(`${this.baseURL}/api/v1/provider-keys/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify(payload),
        })
        if (!resp.ok) await handleError(resp)
        const body = (await resp.json()) as { provider_key: unknown }
        return parseProviderKey(body.provider_key)
    }

    async rotateSecret(id: string, credentialSecretBase64: string, csrfToken: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/api/v1/provider-keys/${encodeURIComponent(id)}/secret`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ credential_secret_base64: credentialSecretBase64 }),
        })
        if (!resp.ok) await handleError(resp)
    }

    // listModels fetch les modèles dispo pour cette ProviderKey (Routing v2).
    // Toujours 200 côté backend ; supported=false → l'UI retombe sur saisie
    // libre (Bedrock, air-gap, credential invalide).
    async listModels(id: string): Promise<ProviderKeyModels> {
        const resp = await fetch(
            `${this.baseURL}/api/v1/provider-keys/${encodeURIComponent(id)}/models`,
            { credentials: 'include' },
        )
        if (!resp.ok) await handleError(resp)
        const body = (await resp.json()) as {
            supported: boolean
            models: { id: string; display_name: string }[]
            error?: string
        }
        const models: ModelInfo[] = (body.models ?? []).map((m) => ({
            id: m.id,
            displayName: m.display_name,
        }))
        return { supported: body.supported, models, error: body.error }
    }

    async remove(id: string, csrfToken: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/api/v1/provider-keys/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'X-CSRF-Token': csrfToken },
        })
        if (!resp.ok) await handleError(resp)
    }
}
